"use client";

import { useEffect, useState } from "react";
import { useCatalogViewport } from "./catalogViewport";
import { FADE_CYCLE_CROSSFADE_MS, FADE_CYCLE_INTERVAL_MS } from "./FadeCyclePreview";

export type BorderFrame = { color: string; bg: string };

/** Same circular frames as the Free Studio Borders & Frames row. */
export default function BorderFrameCyclePreview({
  frames,
  cutoutSrc,
  intervalMs = FADE_CYCLE_INTERVAL_MS,
}: {
  frames: BorderFrame[];
  cutoutSrc: string;
  intervalMs?: number;
}) {
  const slides = frames.filter((frame) => frame.color);
  const { ref, inView } = useCatalogViewport();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!inView) setIndex(0);
  }, [inView]);

  useEffect(() => {
    if (!inView || slides.length < 2) return;
    const id = window.setInterval(() => {
      setIndex((current) => (current + 1) % slides.length);
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [inView, slides.length, intervalMs]);

  if (slides.length === 0) {
    return <div className="h-full w-full bg-gray-100" />;
  }

  return (
    <div ref={ref} className="absolute inset-0 overflow-hidden bg-gray-100">
      {slides.map((frame, i) => {
        const visible = (inView ? index : 0) === i;
        return (
          <div
            key={frame.color}
            className="absolute inset-0 flex items-center justify-center"
            style={{
              opacity: visible ? 1 : 0,
              transition: `opacity ${FADE_CYCLE_CROSSFADE_MS}ms ease`,
            }}
            aria-hidden={!visible}
          >
            <div
              className="flex aspect-square w-[88%] items-center justify-center rounded-full"
              style={{ background: frame.color }}
            >
              <div className="relative aspect-square w-[86%] overflow-hidden rounded-full">
                <div className="absolute inset-0" style={{ background: frame.bg }} />
                <img
                  src={cutoutSrc}
                  alt=""
                  className="relative h-full w-full object-contain object-bottom"
                  draggable={false}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
